/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaApiUndo.ts
 * FILE_ID: JOBHUB-QA-API-UNDO-001
 * PURPOSE: Wrap QA writes so invertible calls register one undo entry.
 * LANDMINE: Compensating thunks call the raw api, not this wrap, so a
 *      busy stack cannot ping-pong. Deletes are not wrapped.
 */
import type { QueryClient } from '@tanstack/react-query'
import type { UndoEntry } from '@quotech/ui'
import { areaItems, checklistGroups } from '../../qa/qaApiHelpers'
import type { QaApi, QaArea, QaAreaExecution, QaInspectionSummary, QaItemChangeRequest, QaItemStatus } from '../../qa/qaApi'

export function wrapQaApiWithUndo(
  api: QaApi,
  queryClient: QueryClient,
  register: (entry: UndoEntry) => void,
  getActiveAreaId?: () => string | null,
): QaApi {
  const execution = () => {
    const id = getActiveAreaId?.()
    if (id) {
      return queryClient.getQueryData<QaAreaExecution>(['qa-area-exec', id])
    }
    const cache = queryClient.getQueriesData<QaAreaExecution>({ queryKey: ['qa-area-exec'] })
    return cache.map(([, value]) => value).find(Boolean)
  }
  const areas = () => {
    const cache = queryClient.getQueriesData<QaArea[]>({ queryKey: ['qa-areas'] })
    return cache.flatMap(([, value]) => value ?? [])
  }
  const inspections = () => {
    const cache = queryClient.getQueriesData<QaInspectionSummary[]>({ queryKey: ['qa-inspections'] })
    return cache.flatMap(([, value]) => value ?? [])
  }
  const itemOf = (itemId: string) => {
    const area = execution()
    return area ? areaItems(area).find((item) => item.id === itemId) : undefined
  }

  return {
    ...api,
    async renameArea(areaId, name) {
      const previous = areas().find((area) => area.id === areaId)?.area_name
      const result = await api.renameArea(areaId, name)
      if (previous && previous !== name) {
        register({
          label: 'Rename area',
          undo: () => api.renameArea(areaId, previous),
          redo: () => api.renameArea(areaId, name),
        })
      }
      return result
    },
    async renameInspection(inspectionId, name) {
      const previous = inspections().find((row) => row.id === inspectionId)?.inspection_name
      const result = await api.renameInspection(inspectionId, name)
      if (previous && previous !== name) {
        register({
          label: 'Rename QA list',
          undo: () => api.renameInspection(inspectionId, previous),
          redo: () => api.renameInspection(inspectionId, name),
        })
      }
      return result
    },
    async renameChecklist(checklistId, title) {
      const previous = execution()
        ? checklistGroups(execution()!).find((group) => group.checklist.id === checklistId)?.name
        : undefined
      const result = await api.renameChecklist(checklistId, title)
      if (previous && previous !== title) {
        register({
          label: 'Rename checklist',
          undo: () => api.renameChecklist(checklistId, previous),
          redo: () => api.renameChecklist(checklistId, title),
        })
      }
      return result
    },
    async setItemStatus(itemId, status, overrideReason) {
      const previous = itemOf(itemId)?.status
      const result = await api.setItemStatus(itemId, status, overrideReason)
      if (previous && previous !== status) {
        register({
          label: 'Change item status',
          undo: () => api.setItemStatus(itemId, previous),
          redo: () => api.setItemStatus(itemId, status),
        })
      }
      return result
    },
    async setAreaWorkStatus(areaId, workStatus) {
      const previous = (areas().find((area) => area.id === areaId)?.work_status
        ?? execution()?.work_status) as QaItemStatus | undefined
      const result = await api.setAreaWorkStatus(areaId, workStatus)
      if (previous && previous !== workStatus) {
        register({
          label: 'Change area status',
          undo: () => api.setAreaWorkStatus(areaId, previous),
          redo: () => api.setAreaWorkStatus(areaId, workStatus),
        })
      }
      return result
    },
    async assignArea(areaId, userId) {
      const previous = areas().find((area) => area.id === areaId)?.assigned_to ?? null
      const result = await api.assignArea(areaId, userId)
      if (previous !== userId) {
        register({
          label: 'Assign area',
          undo: () => api.assignArea(areaId, previous),
          redo: () => api.assignArea(areaId, userId),
        })
      }
      return result
    },
    async assignItem(itemId, userId) {
      const previous = itemOf(itemId)?.assigned_to ?? null
      const result = await api.assignItem(itemId, userId)
      if (previous !== userId) {
        register({
          label: 'Assign check',
          undo: () => api.assignItem(itemId, previous),
          redo: () => api.assignItem(itemId, userId),
        })
      }
      return result
    },
    async setHoldPoint(itemInstanceId, value) {
      const previous = !!itemOf(itemInstanceId)?.has_hold_point
      const result = await api.setHoldPoint(itemInstanceId, value)
      if (previous !== value) {
        register({
          label: 'Change hold point',
          undo: () => api.setHoldPoint(itemInstanceId, previous),
          redo: () => api.setHoldPoint(itemInstanceId, value),
        })
      }
      return result
    },
    async setSignoffRequired(itemInstanceId, value) {
      const previous = !!itemOf(itemInstanceId)?.hold_point_config?.signoff_required
      const result = await api.setSignoffRequired(itemInstanceId, value)
      if (previous !== value) {
        register({
          label: 'Change sign-off',
          undo: () => api.setSignoffRequired(itemInstanceId, previous),
          redo: () => api.setSignoffRequired(itemInstanceId, value),
        })
      }
      return result
    },
    async setReading(itemId, capturedValue, capturedUnit) {
      const item = itemOf(itemId)
      const result = await api.setReading(itemId, capturedValue, capturedUnit)
      register({
        label: 'Change reading',
        undo: () => api.setReading(itemId, item?.captured_value ?? '', item?.captured_unit),
        redo: () => api.setReading(itemId, capturedValue, capturedUnit),
      })
      return result
    },
    async verifyAreaCompletion(areaId, verified) {
      const result = await api.verifyAreaCompletion(areaId, verified)
      register({
        label: 'Verify area',
        undo: () => api.verifyAreaCompletion(areaId, !verified),
        redo: () => api.verifyAreaCompletion(areaId, verified),
      })
      return result
    },
    async verifyItemCompletion(itemId, verified) {
      const result = await api.verifyItemCompletion(itemId, verified)
      register({
        label: 'Verify check',
        undo: () => api.verifyItemCompletion(itemId, !verified),
        redo: () => api.verifyItemCompletion(itemId, verified),
      })
      return result
    },
    async reorderItems(order) {
      const previous = execution()
        ? areaItems(execution()!).map((item, index) => ({
          id: item.id,
          sort_order: item.sort_order ?? index,
        }))
        : []
      const result = await api.reorderItems(order)
      if (previous.length) {
        register({
          label: 'Reorder checks',
          undo: () => api.reorderItems(previous),
          redo: () => api.reorderItems(order),
        })
      }
      return result
    },
    async deleteItem(itemInstanceId) {
      const result = await api.deleteItemForUndo(itemInstanceId)
      const deletionId = result && typeof result === 'object' && 'deletion_id' in result
        ? result.deletion_id
        : undefined
      if (deletionId) {
        const box = { id: deletionId }
        const areaId = getActiveAreaId?.()
        const refresh = () => {
          if (areaId) void queryClient.invalidateQueries({ queryKey: ['qa-area-exec', areaId] })
        }
        register({
          label: 'Delete row',
          undo: async () => {
            await api.restoreRowDeletion(box.id)
            refresh()
          },
          redo: async () => {
            const again = await api.deleteItemForUndo(itemInstanceId)
            if (again && typeof again === 'object' && 'deletion_id' in again && again.deletion_id) {
              box.id = again.deletion_id
            }
            refresh()
          },
        })
      }
      return result as QaItemChangeRequest | void
    },
    async addStandardChecks(checklistId, key) {
      const result = await api.addStandardChecks(checklistId, key)
      const ids = result.created.map((row) => row.id).filter(Boolean)
      if (ids.length) {
        const box = { ids }
        register({
          label: 'Add standard checks',
          undo: () => Promise.all(box.ids.map((id) => api.deleteItem(id))),
          redo: async () => {
            const again = await api.addStandardChecks(checklistId, key)
            box.ids = again.created.map((row) => row.id).filter(Boolean)
            return again
          },
        })
      }
      return result
    },
  }
}
